import { formatDate as formatAppDate, formatNumber } from "@/utils/formatters";
import type { InstallmentPurchase } from "@/domains/installments/types";

interface InstallmentAccountReference {
  account_id: number;
}

export const getInstallmentAccountIds = (
  installments: readonly InstallmentAccountReference[],
): Set<number> => new Set(installments.map(({ account_id }) => account_id));

export const filterAccountsWithInstallments = <T extends { id: number }>(
  accounts: readonly T[],
  installmentAccountIds: ReadonlySet<number>,
): T[] => accounts.filter(({ id }) => installmentAccountIds.has(id));

export const keepValidInstallmentAccountIds = (
  selectedAccountIds: readonly string[],
  installmentAccountIds: ReadonlySet<number>,
): string[] => selectedAccountIds.filter((id) => installmentAccountIds.has(Number(id)));

export const formatShortDate = (dateValue: string): string => {
  if (!dateValue) return "-";
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "-";
  return formatAppDate(date, undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

export const formatInterestRate = (rate: number): string =>
  `${formatNumber(rate, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;

export const addMonths = (dateValue: string, monthsToAdd: number): string => {
  const [year, month, day] = dateValue.split("-").map(Number);
  const date = new Date(year, (month ?? 1) - 1 + monthsToAdd, day ?? 1);
  const nextYear = date.getFullYear();
  const nextMonth = String(date.getMonth() + 1).padStart(2, "0");
  const nextDay = String(date.getDate()).padStart(2, "0");
  return `${nextYear}-${nextMonth}-${nextDay}`;
};

export const getInstallmentDebtSummary = (activePurchases: InstallmentPurchase[]) => {
  const accountTotals = new Map<string, {
    accountName: string;
    amount: number;
    count: number;
  }>();

  const totals = activePurchases.reduce(
    (result, purchase) => {
      const outstanding = Math.max(0, purchase.remainingBalance);
      result.upcomingTotal += purchase.monthlyPayment;
      result.outstandingTotal += outstanding;

      if (outstanding > 0) {
        const account = accountTotals.get(purchase.accountId);
        if (account) {
          account.amount += outstanding;
          account.count += 1;
        } else {
          accountTotals.set(purchase.accountId, {
            accountName: purchase.accountName,
            amount: outstanding,
            count: 1,
          });
        }
      }

      return result;
    },
    { upcomingTotal: 0, outstandingTotal: 0 },
  );

  return {
    ...totals,
    accountDebtSummary: [...accountTotals.values()].sort(
      (a, b) => b.amount - a.amount,
    ),
  };
};

// Stable per-id hash so the "random" pick does not change between renders.
const hashId = (id: string) => [...id].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 7);

/**
 * Picks a pseudo-random palette color per installment, never repeating a color among the given
 * ids while the palette has room. Only past palette size do colors start to repeat.
 */
export const assignInstallmentColors = (ids: string[], palette: string[]): Map<string, string> => {
  const assigned = new Map<string, string>();
  if (palette.length === 0) return assigned;

  let used = new Set<number>();
  [...ids].sort().forEach((id) => {
    if (used.size === palette.length) used = new Set();
    let index = hashId(id) % palette.length;
    while (used.has(index)) index = (index + 1) % palette.length;
    used.add(index);
    assigned.set(id, palette[index]);
  });
  return assigned;
};
